/**
 * Notification Bell Component
 * Displays notification icon with unread count badge and dropdown
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationCenter } from './NotificationCenter';
import { Button } from './button';
import { refreshNotificationSummary, useNotificationSummary } from '@/lib/notificationSummaryStore';

interface NotificationBellProps {
  onNavigate?: (tab: string) => void;
}

/**
 * The bell only shows the summary. Polling and the desktop notifications live
 * in StaffNotificationsHost (TRUS-668), mounted once in the staff shell, so
 * they keep running while the sidebar is collapsed and this bell is not rendered.
 */
export function NotificationBell({ onNavigate }: NotificationBellProps = {}) {
  const { user } = useAuth();
  const summary = useNotificationSummary();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate dropdown position within viewport bounds
  const updatePosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 384; // w-96 = 384px
      const dropdownHeight = 600; // max-h-[600px]
      const gap = 8;

      // Calculate ideal position (right-aligned with button)
      let left = rect.left - dropdownWidth + rect.width;
      let top = rect.bottom + gap;

      // Ensure dropdown stays within viewport horizontally
      if (left < gap) {
        left = gap; // Too far left, align with left edge
      } else if (left + dropdownWidth > window.innerWidth - gap) {
        left = window.innerWidth - dropdownWidth - gap; // Too far right
      }

      // Ensure dropdown stays within viewport vertically
      if (top + dropdownHeight > window.innerHeight - gap) {
        // Try to show above button
        const topAbove = rect.top - dropdownHeight - gap;
        if (topAbove >= gap) {
          // Enough space above
          top = topAbove;
        } else {
          // Not enough space above or below, position at top of viewport
          top = gap;
        }
      }

      // Ensure top is never negative
      if (top < gap) {
        top = gap;
      }

      setPosition((prev) => {
        // Only update if position actually changed to prevent infinite re-renders
        if (prev.top !== top || prev.left !== left) {
          return { top, left };
        }
        return prev;
      });
    }
  };

  // Update position when opening
  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if refs aren't ready
      if (!dropdownRef.current || !buttonRef.current) {
        return;
      }

      // Check if click is outside both the dropdown and the button
      const isOutsideDropdown = !dropdownRef.current.contains(target);
      const isOutsideButton = !buttonRef.current.contains(target);

      if (isOutsideDropdown && isOutsideButton) {
        setIsOpen(false);
      }
    };

    // Use a timeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [isOpen]);

  // Handle notification refresh from NotificationCenter
  const handleRefresh = () => {
    void refreshNotificationSummary();
  };

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (!isOpen) void refreshNotificationSummary();
    setIsOpen(!isOpen);
  };

  if (!user) return null;

  const unreadCount = summary?.unread_count || 0;
  const hasUrgent = (summary?.urgent_unread_count || 0) > 0;

  return (
    <>
      <Button
        ref={buttonRef}
        variant="ghost"
        size="sm"
        className={`relative h-9 w-9 p-0 ${
          hasUrgent ? 'animate-pulse' : ''
        }`}
        onClick={handleToggle}
        aria-label={`Notifications (${unreadCount} unread)`}
        title={`${unreadCount} unread notifications`}
      >
        <Bell className={`h-5 w-5 ${hasUrgent ? 'text-red-500' : ''}`} />
        {unreadCount > 0 && (
          <span
            className={`absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white ${
              hasUrgent ? 'bg-red-500' : 'bg-blue-500'
            }`}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && createPortal(
        <NotificationCenter
          ref={dropdownRef}
          summary={summary}
          onClose={() => setIsOpen(false)}
          onRefresh={handleRefresh}
          position={position}
          onNavigate={onNavigate}
        />,
        document.body
      )}
    </>
  );
}
